import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { PolicyChangeService } from 'gw-capability-gateway';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';

import MLCancellationsReportAdditionalFilterMessages from './MLCancellationsReportAdditionalFilter';

import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLCancellationsReportComponent.messages';
import metadata from './MLCancellationsReportComponent.metadata.json5';

const MLCancellationsReportComponent = (props) => {
    const {
        rowLimit,
        isDashboard,
        isCompactView,
        getCustomerDisplayNameRevert,
        history,
        viewModelService,
        convertLocalDateToDate
    } = props;
    const { gatewayParamConfig } = appConfig;
    const translator = useTranslator();

    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const initiatorTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('CancellationSource');
    const cancelReasonTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('ReasonCode');

    const loadCancellationsData = useCallback(async () => {
        updateIsLoading(true);

        const response = isDashboard
            ? await PolicyChangeService.getCancelJobsForCurrentUser(gatewayParamConfig.jobsCancelledInLastXDays, rowLimit, filterData, authHeader)
            : await PolicyChangeService.getCancelJobsForCurrentUser(0, null, filterData, authHeader);

        updateFilteredJobs(response);
        updateIsLoading(false);
        updateResultsRowCount(response.length);
    }, [authHeader, gatewayParamConfig.jobsCancelledInLastXDays, isDashboard, rowLimit, filterData]);

    useEffect(() => {
        loadCancellationsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFilter = () => {
        loadCancellationsData();
    };

    const onClearFilters = () => {
        updateFilterData({});
    };

    const isDateInPast = (date)=> {
        if (!date) {
            return
        };
        return moment(date).isBefore(moment(), 'd');
    };

    const renderCancelDateCell = (item) => {
        let cancelDateClass = '';
        if (isDateInPast(item.cancelDate_ML)) {
            cancelDateClass = 'error-text';
        }
        return item.cancelDate_ML ? <span className={cancelDateClass}>{convertLocalDateToDate(item.cancelDate_ML)}</span> : '-';
    };

    const renderCustomerName = (item) => {
        if(_.isUndefined(item.displayName_ML)) {
            return '-'
        }
        return (
            <Link to={`/accounts/${item.accountNumber}/summary`} className={styles.linkCellValue}>
                {_.capitalize(getCustomerDisplayNameRevert(item.accountHolder_ML))}
            </Link>
        )
    };

    const renderProductCell = (item) => {
        return _.get(item, 'product.productName') ? item.product.productName : '-';
    };

    const renderPolicyCell = (item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderInitiatorCell = (item) => {
        return item.initiator_ML ? item.initiator_ML : '-';
    };

    const renderCancelReasonCell = (item) => {
        if(_.isUndefined(item.cancelReason_ML)) {
            return '-'
        }
        return (
            <Link to={`/cancellation/${item.jobNumber}/summary`} className={styles.linkCellValue}>
                {item.cancelReason_ML}
            </Link>
        )
    };

    const onViewAllButtonClicked = () => {
        const reportView = 'cancellations-report';
        history.push('/activity-reports', {reportView : reportView} );
    }

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: translator(messages.loading)
        },
        mlJobResultsFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            additionalFiltersComponent: MLCancellationsReportAdditionalFilterMessages,
            additionalFilterProps: {
                initiatorTypelist,
                cancelReasonTypelist
            },
            hideFields: ['PRODUCT']
        },
        cancellationsReportTable: {
            data: _.orderBy(filteredJobs, 'cancelDate_ML', 'asc'),
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: translator(messages.noCancellationsFound),
            showPagination: !isCompactView,
            tableLabel: translator(messages.cancellations)
        },
        viewAllCancellationsButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_cancellations"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderCancelDateCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderInitiatorCell,
            renderCancelReasonCell
        },
        resolveClassNameMap: styles
    };
    
    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLCancellationsReportComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    convertLocalDateToDate: PropTypes.func.isRequired
}

export default withRouter(withViewModelService(MLCancellationsReportComponent));
